/**
 * useCycleLineageEvents
 *
 * Builds the lineage timeline event list for a given cycle.
 * Reads from degapProcessStore, sessionStore, and trackStore.
 *
 * Events derived:
 *   – cycle_created         : when the cycle was created
 *   – cycle_template_applied: if a template was applied at creation
 *   – cycle_phase_transition: one event per completed phase in phaseHistory
 *   – cycle_track_added     : one event per track in the cycle
 *   – cycle_session_run     : one event per session that ran under the cycle
 *   – cycle_output_published: when results were published
 *
 * Crosswise: cycle provenance shows the items that moved through it via
 *            cycle_session_run events linking to individual session pages.
 */

import { useMemo } from 'react'
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore'
import { useSessionStore } from '../../../stores/prioritization/sessionStore'
import { useTrackStore } from '../../../stores/prioritization/trackStore'
import { toTimelineDate, type TimelineEvent } from './LineageTimeline'

function td(v: unknown) {
  return toTimelineDate(v)
}

export function useCycleLineageEvents(cycleId: string): TimelineEvent[] {
  const cycle = useDegapProcessStore((s) => s.cycles.find((c) => c.id === cycleId))
  const allSessions = useSessionStore((s) => s.sessions)
  const allTracks = useTrackStore((s) => s.tracks)

  return useMemo(() => {
    if (!cycle) return []

    const events: TimelineEvent[] = []

    // 1. Created
    const createdAt = td(cycle.createdAt)
    if (createdAt) {
      events.push({
        id: 'cycle-created',
        type: 'cycle_created',
        timestamp: createdAt,
        label: 'Cycle Created',
        detail: cycle.name,
        actor: (cycle as any).createdBy ?? cycle.ownerId,
      })
    }

    // 2. Template applied
    if ((cycle as any).appliedTemplateId) {
      events.push({
        id: 'cycle-template',
        type: 'cycle_template_applied',
        timestamp: createdAt ?? new Date(),
        label: 'Template Applied',
        detail: String((cycle as any).appliedTemplateId).slice(0, 8),
        isEstimated: !createdAt,
      })
    }

    // 3. Phase transitions from phaseHistory
    const phaseHistory: { phase: string; enteredAt?: unknown; completedAt?: unknown }[] =
      (cycle as any).phaseHistory ?? []
    phaseHistory.forEach((entry, idx) => {
      const ts = td(entry.completedAt ?? entry.enteredAt)
      if (!ts) return
      events.push({
        id: `phase-${entry.phase}-${idx}`,
        type: 'cycle_phase_transition',
        timestamp: ts,
        label: entry.completedAt ? `${entry.phase} Complete` : `${entry.phase} Started`,
        sublabel: entry.phase,
        detail: entry.completedAt ? 'Phase completed' : 'Phase entered',
      })
    })

    // 4. Tracks added under this cycle
    const cycTracks = allTracks.filter((t) => t.cycleId === cycleId)
    cycTracks.forEach((track) => {
      const ts = td((track as any).createdAt) ?? createdAt
      if (!ts) return
      events.push({
        id: `track-added-${track.id}`,
        type: 'cycle_track_added',
        timestamp: ts,
        label: track.name ?? 'Track Added',
        linkedId: track.id,
        linkedLabel: track.name ?? 'View track',
        linkedPath: `/prioritization/tracks/${track.id}`,
        isEstimated: !td((track as any).createdAt),
      })
    })

    // 5. Sessions that ran under this cycle (crosswise link to session detail)
    const cycleSessions = allSessions.filter(
      (s) => (s as any).cycleId === cycleId || (s as any).cycle_id === cycleId,
    )
    cycleSessions.forEach((session) => {
      const ts = td((session as any).createdAt)
      if (!ts) return
      events.push({
        id: `session-run-${session.id}`,
        type: 'cycle_session_run',
        timestamp: ts,
        label: (session as any).name ?? 'Session Run',
        detail: (session as any).state
          ? `State: ${(session as any).state}`
          : undefined,
        linkedId: session.id,
        linkedLabel: (session as any).name ?? 'View session',
        linkedPath: `/prioritization/sessions/${session.id}`,
      })
    })

    // 6. Output published (use last-session publish time or cycle updatedAt as estimate)
    const publishedAt =
      td((cycle as any).publishedAt) ??
      td((cycle as any).completedAt) ??
      td((cycle as any).outputPublishedAt)
    if (publishedAt) {
      events.push({
        id: 'cycle-output-published',
        type: 'cycle_output_published',
        timestamp: publishedAt,
        label: 'Output Published',
        detail: cycle.status === 'archived' ? 'Cycle closed' : 'Results finalised',
      })
    }

    // Dedupe + sort
    const seen = new Set<string>()
    return events
      .filter((e) => {
        if (seen.has(e.id)) return false
        seen.add(e.id)
        return true
      })
      .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
  }, [cycle, allSessions, allTracks, cycleId])
}
